import { useState } from 'react';
import { Button, Card, Col, DatePicker, Descriptions, InputNumber, Row, Statistic, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, TeamOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import AppLayout from '../../layouts/app.layout';
import axios from '../../config/axios';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { Title, Text } = Typography;

interface CommissionPeriod {
	month: string;
	month_label: string;
	total_sold: number;
	excluded_recovered: number;
	excluded_defaulted: number;
	qualifying: number;
	rate: string;
	commission: number;
}

interface CommissionData {
	commission_per_device: number;
	current_month: CommissionPeriod;
	previous_month: CommissionPeriod;
	total_payable: number;
}

const fmt = (n: number) => `UGX ${formatNumber(n)}`;

const PeriodCard = ({ period, color }: { period: CommissionPeriod; color: string }) => (
	<Card
		title={
			<div>
				<div className="font-semibold">{period.month_label}</div>
				<div className="text-xs font-normal text-gray-500">{period.rate} payout</div>
			</div>
		}
		className="shadow-sm h-full"
	>
		<Descriptions column={1} size="small" bordered>
			<Descriptions.Item label="Total Sold">
				<span className="font-semibold">{period.total_sold}</span>
			</Descriptions.Item>
			<Descriptions.Item label={<span className="flex items-center gap-1"><CloseCircleOutlined className="text-red-500" /> Excluded — Recovered</span>}>
				<span className="text-red-500">{period.excluded_recovered}</span>
			</Descriptions.Item>
			<Descriptions.Item label={<span className="flex items-center gap-1"><CloseCircleOutlined className="text-orange-500" /> Excluded — Defaulted</span>}>
				<span className="text-orange-500">{period.excluded_defaulted}</span>
			</Descriptions.Item>
			<Descriptions.Item label={<span className="flex items-center gap-1"><CheckCircleOutlined className="text-green-500" /> Qualifying Devices</span>}>
				<span className="font-semibold text-green-600">{period.qualifying}</span>
			</Descriptions.Item>
		</Descriptions>
		<div className={`mt-4 rounded-lg p-4 ${color}`}>
			<div className="text-sm text-gray-600">{period.rate} Commission</div>
			<div className="text-2xl font-bold">{fmt(period.commission)}</div>
		</div>
	</Card>
);

const CommissionReport = () => {
	const [selectedMonth, setSelectedMonth] = useState<Dayjs>(dayjs());
	const [commissionPerDevice, setCommissionPerDevice] = useState<number>(10000);
	const [queryParams, setQueryParams] = useState<{ month: string; commission_per_device: number } | null>(null);

	const { data, isLoading, error } = useQuery<CommissionData>({
		queryKey: ['commission-report', queryParams],
		queryFn: async () => {
			const res = await axios.get('/admin/reports/commission', {
				params: queryParams,
				withCredentials: true,
			});
			return res.data.results;
		},
		enabled: !!queryParams,
	});

	if (error) handleAPIError(error);

	const handleCalculate = () => {
		setQueryParams({
			month: selectedMonth.format('YYYY-MM'),
			commission_per_device: commissionPerDevice,
		});
	};

	return (
		<AppLayout title="Commission Report" subtitle="Calculate agent commission per month">
			{/* Controls */}
			<Card className="mb-6 shadow-sm">
				<div className="flex flex-wrap items-end gap-4">
					<div>
						<div className="mb-1 text-sm font-medium text-gray-700">Select Month</div>
						<DatePicker
							picker="month"
							value={selectedMonth}
							onChange={(val) => val && setSelectedMonth(val)}
							format="MMMM YYYY"
							allowClear={false}
						/>
					</div>
					<div>
						<div className="mb-1 text-sm font-medium text-gray-700">Commission per Device (UGX)</div>
						<InputNumber
							value={commissionPerDevice}
							onChange={(val) => val && setCommissionPerDevice(val)}
							min={0}
							formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
							parser={(val) => Number(val?.replace(/,/g, '') ?? 0)}
							style={{ width: 180 }}
						/>
					</div>
					<Button type="primary" onClick={handleCalculate} loading={isLoading}>
						Calculate
					</Button>
				</div>
			</Card>

			{/* Results */}
			{data && (
				<div className="space-y-6">
					{/* Summary */}
					<Card className="shadow-sm">
						<Row gutter={[24, 16]}>
							<Col xs={24} sm={8}>
								<Statistic
									title="Commission per Device"
									value={data.commission_per_device}
									prefix="UGX"
									formatter={(val) => formatNumber(Number(val))}
								/>
							</Col>
							<Col xs={24} sm={8}>
								<Statistic
									title={`70% Payout — ${data.current_month.month_label}`}
									value={data.current_month.commission}
									prefix="UGX"
									formatter={(val) => formatNumber(Number(val))}
									valueStyle={{ color: '#1890ff' }}
								/>
							</Col>
							<Col xs={24} sm={8}>
								<Statistic
									title={`30% Balance — ${data.previous_month.month_label}`}
									value={data.previous_month.commission}
									prefix="UGX"
									formatter={(val) => formatNumber(Number(val))}
									valueStyle={{ color: '#722ed1' }}
								/>
							</Col>
						</Row>
						<div className="mt-4 rounded-lg bg-green-50 p-4 border border-green-200">
							<div className="flex items-center gap-2">
								<TeamOutlined className="text-green-600 text-lg" />
								<div>
									<div className="text-sm text-gray-600">Total Payable</div>
									<div className="text-3xl font-bold text-green-700">{fmt(data.total_payable)}</div>
								</div>
							</div>
						</div>
					</Card>

					{/* Period breakdown */}
					<div>
						<Title level={5} className="mb-3 text-gray-600">Breakdown</Title>
						<Row gutter={[16, 16]}>
							<Col xs={24} md={12}>
								<PeriodCard period={data.current_month} color="bg-blue-50" />
							</Col>
							<Col xs={24} md={12}>
								<PeriodCard period={data.previous_month} color="bg-purple-50" />
							</Col>
						</Row>
					</div>

					{/* Note */}
					<Text type="secondary" className="text-xs">
						* Defaulted: any installment due on or before end of month that was not paid by month end.
						* Checks run at time of calculation — re-run at month end for final figures.
					</Text>
				</div>
			)}

			{!data && !isLoading && (
				<Card className="shadow-sm text-center py-12">
					<Text type="secondary">Select a month and commission amount, then click Calculate.</Text>
				</Card>
			)}
		</AppLayout>
	);
};

export default CommissionReport;
